import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, Check } from "lucide-react";

const TASKS = [
  { key: "assist", label: "Ask anything", placeholder: "e.g. Draft a follow-up strategy for a cold lead in fintech" },
  { key: "draft_email", label: "Draft email", placeholder: "e.g. Follow-up for demo request from Acme Corp" },
  { key: "draft_sms", label: "Draft SMS", placeholder: "e.g. Reminder to Anna about tomorrow's 3pm call" },
  { key: "summarize", label: "Summarize", placeholder: "Paste notes / activity to summarize into a brief" },
  { key: "score_lead", label: "Score lead", placeholder: "Paste contact info + activity to score 0–100" },
];

export default function AIAssistantDrawer({ open, onOpenChange, initialContext = "" }) {
  const [task, setTask] = useState("assist");
  const [prompt, setPrompt] = useState("");
  const [context, setContext] = useState(initialContext);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    if (!prompt.trim()) {
      toast.error("Add a prompt first");
      return;
    }
    setLoading(true);
    setReply("");
    try {
      const { data } = await api.post("/ai/ask", { prompt, context, task });
      setReply(data.reply || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI request failed");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(reply);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg bg-white border-l-2 border-l-[var(--peak-blue-deep)] rounded-none p-0"
        data-testid="ai-drawer"
      >
        <SheetHeader className="p-6 border-b border-[var(--peak-border)]">
          <SheetTitle className="font-heading font-black text-2xl tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[var(--peak-blue-deep)]" />
            AI COPILOT
          </SheetTitle>
          <div className="section-label mt-1">Claude Sonnet 4.5 · Peak CRM intelligence</div>
        </SheetHeader>

        <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(100vh-100px)]">
          <div>
            <div className="section-label mb-2">Task</div>
            <div className="grid grid-cols-2 gap-2">
              {TASKS.map((t) => (
                <button
                  key={t.key}
                  data-testid={`ai-task-${t.key}`}
                  onClick={() => setTask(t.key)}
                  className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${
                    task === t.key
                      ? "bg-[var(--peak-blue-deep)] text-white border-[var(--peak-blue-deep)]"
                      : "bg-white border-[var(--peak-border)] hover:border-[var(--peak-ink)]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="section-label mb-2">Prompt</div>
            <textarea
              data-testid="ai-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={TASKS.find((t) => t.key === task)?.placeholder}
              rows={3}
              className="w-full border border-[var(--peak-border)] bg-white p-3 text-sm focus:outline-none focus:border-[var(--peak-ink)]"
            />
          </div>

          <div>
            <div className="section-label mb-2">Context (optional)</div>
            <textarea
              data-testid="ai-context-input"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Paste contact info, deal notes, or any relevant details"
              rows={3}
              className="w-full border border-[var(--peak-border)] bg-white p-3 text-sm focus:outline-none focus:border-[var(--peak-ink)]"
            />
          </div>

          <button
            data-testid="ai-run-btn"
            onClick={run}
            disabled={loading}
            className="w-full bg-[var(--peak-accent)] text-white font-bold uppercase tracking-wider px-4 py-3 hover:bg-[var(--peak-ink)] disabled:opacity-60 flex items-center justify-center gap-2 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? "Thinking…" : "Generate"}
          </button>

          {reply && (
            <div className="border border-[var(--peak-ink)] bg-[var(--peak-bg)]" data-testid="ai-reply">
              <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--peak-ink)] bg-white">
                <span className="section-label">Response</span>
                <button
                  data-testid="ai-copy-btn"
                  onClick={copy}
                  className="flex items-center gap-1 text-xs font-semibold hover:text-[var(--peak-accent)]"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <pre className="p-4 whitespace-pre-wrap text-sm font-body leading-relaxed">{reply}</pre>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
